<template>
  <div class="verify-email-page">
    
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="auth-container">
      <header>
        <div class="logo-container">
          <img src="@/assets/img/quitly-blue.svg" alt="Quitly" class="logo" />
        </div>
        <h2 id="page-title" class="page-title">E-Mail-Verifizierung</h2>
      </header>
      
      <main id="main-content" role="main" aria-labelledby="page-title">
        <div v-if="isLoading" class="loading-container" aria-live="polite">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Wird geladen...</span>
          </div>
          <p class="mt-3">E-Mail wird verifiziert...</p>
        </div>
        
        <div v-else-if="errorMessage" class="error-container" aria-live="assertive">
          <div class="error-icon" aria-hidden="true">
            <font-awesome-icon icon="circle-exclamation" size="4x" class="text-danger" />
          </div>
          <p class="error-text">{{errorMessage}}</p>
          <div class="mt-4">
            <button @click="redirect" class="btn btn-primary">
              {{ 'Zurück zur Anmeldung' }}
              </button>
          </div>
        </div>
        
        <div v-else-if="successMessage" class="success-container" aria-live="polite">
          <div class="success-icon" aria-hidden="true">
            <font-awesome-icon icon="circle-check" size="4x" class="text-success" />
          </div>
          <p class="success-text">{{ successMessage }}</p>
          <div class="mt-4">
            <button @click="redirect" class="btn btn-primary">
              {{ 'Zur Anmeldung' }}
            </button>
          </div>
        </div>
        
        <div v-else-if="token" class="verification-pending-container">
          <div class="verify-icon" aria-hidden="true">
            <font-awesome-icon icon="envelope" size="4x" class="text-primary" />
          </div>
          <h3 class="verify-title mt-3">E-Mail bestätigen</h3>
          <p class="verify-text">Bitte bestätige deine E-Mail-Adresse</p>
          <div class="mt-4">
            <button 
              @click="verifyUserEmail" 
              class="btn btn-primary verify-button"
              :disabled="isVerifying"
              aria-busy="isVerifying"
            >
              <span v-if="isVerifying">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isVerifying ? 'Wird verarbeitet...' : 'Klicke hier zur Bestätigung' }}
              </span>
              <span v-else>
                {{ isVerifying ? 'Wird verarbeitet...' : 'Klicke hier zur Bestätigung' }}
              </span>
            </button>
          </div>
        </div>
        
        <div v-else class="error-container" aria-live="assertive">
          <div class="error-icon" aria-hidden="true">
            <font-awesome-icon icon="circle-exclamation" size="4x" class="text-warning" />
          </div>
          <p class="error-text">Token fehlt</p>
          <div class="mt-4">
            <router-link to="/auth/login" class="btn btn-outline-primary">
              {{ 'Zurück zur Anmeldung' }}
            </router-link>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { verifyEmail } from '@/api/auth'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const isLoading = ref(false)
const isVerifying = ref(false)
const errorMessage = ref('')
const verificationSuccess = ref(false)
const token = ref(null)
const successMessage = ref('')

const redirect = () => {
  window.location.href = "quitly://auth/login"
  router.replace("/auth/login")
}

onMounted(async () => {
  if (authStore.isAuthenticated) {
    await authStore.logout()
  }

  token.value = route.query.token
  
  if (!token.value) {
    errorMessage.value = 'Token fehlt'
  }
})

const verifyUserEmail = async () => {
  if (!token.value) {
    errorMessage.value = 'Token fehlt'
    return
  }

  try {
    const response = await verifyEmail(token.value)
    successMessage.value = response?.message || "E-Mail wurde erfolgreich verifiziert"
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Verifizierung fehlgeschlagen'
  }
}
</script>
